import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import SelectSearch from 'react-select-search';
import Fuse from 'fuse.js';
import Loader from 'react-loader-spinner';
import { FiVideo } from 'react-icons/fi';
import axios from 'axios';
import { BASE_URL } from '../constants/config';
import { getTokensFromStore, checkIfTokenExpired } from '../utilities/tokens';
import { refreshTokens } from '../actions/usersActions';
import { useI18n } from '../utilities/i18n';
import { formatKickoff, normalizeAdminMatch } from '../utilities/matchesApi';
import '../styles/selectMatchMedia.css';

function SelectMatchMedia(props) {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [championships, setChampionships] = useState([]);
  const [selectedChampionship, setSelectedChampionship] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);

  const [matches, setMatches] = useState([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  const getAuthHeaders = async () => {
    const isTokenExpired = checkIfTokenExpired();
    if (isTokenExpired) {
      await dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    return {
      Authorization: 'Bearer ' + tokens.access_token,
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };
  };

  const extractList = (payload, keys = []) => {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== 'object') return [];
    for (const key of keys) {
      if (Array.isArray(payload[key])) return payload[key];
      if (payload[key] && Array.isArray(payload[key].data)) return payload[key].data;
    }
    if (Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const fuzzySearch = (options) => {
    const fuse = new Fuse(options, { keys: ['name'], threshold: 0.3 });
    return (value) => {
      if (!value.length) return options;
      return fuse.search(value).map((item) => item.item);
    };
  };

  // Load championships and leagues
  useEffect(() => {
    const load = async () => {
      setLoadingFilters(true);
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(
          BASE_URL.url + '/leagues/1/adminLeagues',
          {},
          { headers }
        );

        const leaguesData = extractList(response.data);
        const champMap = {};

        leaguesData.forEach((item) => {
          const name = item.championat_name || t('noChampionship');
          if (!champMap[name]) {
            champMap[name] = { value: name, name, leagues: [] };
          }
          if (item.league_id > 0) {
            champMap[name].leagues.push({
              value: item.league_id,
              name: item.league_name,
              seasons: item.seasons || [],
              ...item,
            });
          }
        });

        setChampionships(
          Object.values(champMap)
            .filter((c) => c.leagues.length > 0)
            .map((c, i) => ({ ...c, key: i }))
        );
      } catch (err) {
        console.error('Error loading leagues:', err);
      } finally {
        setLoadingFilters(false);
      }
    };
    load();
  }, []);

  const handleChampionshipChange = (value) => {
    const champ = championships.find((c) => c.value === value);
    setSelectedChampionship(champ || null);
    setLeagues(champ ? champ.leagues : []);
    setSelectedLeague(null);
    setSeasons([]);
    setSelectedSeason(null);
    setStages([]);
    setSelectedStage(null);
    setMatches([]);
  };

  const handleLeagueChange = (value) => {
    const league = leagues.find((l) => String(l.value) === String(value));
    setSelectedLeague(league || null);

    const rawSeasons = league?.seasons || [];
    const preparedSeasons = rawSeasons.map((s, i) => ({
      value: s.season_id || s.id || i,
      name: s.season_name || s.name || t('dqSeasonFallback', { n: i + 1 }),
      key: i,
      ...s,
    }));
    setSeasons(preparedSeasons);
    setSelectedSeason(null);
    setStages([]);
    setSelectedStage(null);
    setMatches([]);
  };

  const handleSeasonChange = async (value) => {
    const season = seasons.find((s) => String(s.value) === String(value));
    setSelectedSeason(season || null);
    setStages([]);
    setSelectedStage(null);
    setMatches([]);

    if (!selectedLeague || !season) return;

    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/getLeagueStages`,
        { league_id: selectedLeague.value, season_id: season.value },
        { headers }
      );

      const stagesData = extractList(response.data);
      setStages(
        stagesData.map((item, i) => ({
          ...item,
          value: item.id,
          name: item.name,
          key: i,
        }))
      );
    } catch (err) {
      console.error('Error loading stages:', err);
      setStages([]);
    }
  };

  const handleStageChange = async (value) => {
    const stage = stages.find((s) => String(s.value) === String(value));
    setSelectedStage(stage || null);
    setMatches([]);

    if (!selectedLeague || !selectedSeason || !stage) return;

    setLoadingMatches(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.post(
        BASE_URL.url + `/leagues/${selectedLeague.value}/matches`,
        { season_id: selectedSeason.value, stage_id: stage.value },
        { headers }
      );

      const matchesData = extractList(response.data);
      const filtered = matchesData
        .filter((m) => m.stage_id === stage.value || m.tour_id === stage.value)
        .map(normalizeAdminMatch)
        .filter((m) => m.id);
      setMatches(filtered);
    } catch (err) {
      console.error('Error loading matches:', err);
      setMatches([]);
    } finally {
      setLoadingMatches(false);
    }
  };

  const renderMatchStatus = (match) => {
    if (match.resheduled) return t('matchPostponed');
    if (!match.status || match.status === 0) return t('stStatusNotStarted');
    if (match.status === 1) return t('stStatusLive');
    if (match.status === 2 || match.status === 3) return t('stStatusFinished');
    return t('stStatusUnknown');
  };

  return (
    <div className="layoutWrapper">
      <div className="matchCenterWrapper select-match-media-page">
        <div className="matchCenterHeader">
          <span className="matchCenterName">{t('mmSelectTitle')}</span>

          <div className="select-match-media-filters">
            <div className="select-match-media-filter-group">
              <label className="select-match-media-label">{t('championship')}</label>
              <SelectSearch
                options={championships}
                value={selectedChampionship?.value || ''}
                onChange={handleChampionshipChange}
                search
                filterOptions={fuzzySearch}
                emptyMessage={t('championshipNotFound')}
                placeholder={t('championshipPlaceholder')}
                disabled={loadingFilters}
              />
            </div>

            <div className="select-match-media-filter-group">
              <label className="select-match-media-label">{t('league')}</label>
              <SelectSearch
                options={leagues}
                value={selectedLeague ? String(selectedLeague.value) : ''}
                onChange={handleLeagueChange}
                search
                filterOptions={fuzzySearch}
                emptyMessage={t('leagueNotFound')}
                placeholder={t('leaguePlaceholder')}
                disabled={!selectedChampionship}
              />
            </div>

            <div className="select-match-media-filter-group">
              <label className="select-match-media-label">{t('stSeason')}</label>
              <SelectSearch
                options={seasons}
                value={selectedSeason ? String(selectedSeason.value) : ''}
                onChange={handleSeasonChange}
                search
                filterOptions={fuzzySearch}
                emptyMessage={t('stSeasonNotFound')}
                placeholder={t('stSeasonPlaceholder')}
                disabled={!selectedLeague}
              />
            </div>

            <div className="select-match-media-filter-group">
              <label className="select-match-media-label">{t('mmStage')}</label>
              <SelectSearch
                options={stages}
                value={selectedStage ? String(selectedStage.value) : ''}
                onChange={handleStageChange}
                search
                filterOptions={fuzzySearch}
                emptyMessage={t('mmStageNotFound')}
                placeholder={t('mmStagePlaceholder')}
                disabled={!selectedSeason}
              />
            </div>
          </div>
        </div>

        <div className="select-match-media-body">
          {loadingMatches && (
            <div className="select-match-media-loader">
              <Loader type="TailSpin" color="#3ad1a0" height={40} width={40} />
            </div>
          )}

          {!loadingMatches && !selectedStage && (
            <div className="select-match-media-empty">{t('mmSelectPrompt')}</div>
          )}

          {!loadingMatches && selectedStage && matches.length === 0 && (
            <div className="select-match-media-empty">{t('stNoMatches')}</div>
          )}

          {!loadingMatches && matches.length > 0 && (
            <div className="select-match-media-matches">
              {matches.map((match) => (
                <div key={match.id} className="select-match-media-match-card">
                  <div className="match-card-date">
                    {formatKickoff(match)}
                  </div>
                  <div className="match-card-teams">
                    <span className="match-card-team">{match.home_team_name || '?'}</span>
                    <span className="match-card-score">
                      {match.status > 0
                        ? `${match.home_team_scores ?? 0}:${match.guest_team_scores ?? 0}`
                        : 'vs'}
                    </span>
                    <span className="match-card-team">{match.guest_team_name || '?'}</span>
                  </div>
                  <div className="match-card-status">{renderMatchStatus(match)}</div>
                  <Link to={`/manage_match_media/${match.id}`} className="match-card-media-btn">
                    <FiVideo style={{ marginRight: 6 }} />
                    {t('mmMedia')}
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SelectMatchMedia;
